import { getProfileImage } from "@shared/hooks/useProfile";
import React from "react";
import { setEventStatus } from "../services/eventFavorite.service";
import { CalendarEvent, EventCategory } from "../types/calendar.types";
import { getUtcOffsetLabel } from "../utils/timezoneUtils";
// import { useAuth } from "@shared/hooks/useAuth";

interface EventCardProps {
  event: CalendarEvent & {
    attendees?: any[];
    userStatus?: { isFavorite: boolean; isAttendee: boolean };
  };
  category?: EventCategory;
  onClick?: (event: CalendarEvent) => void;
  userId?: string;
  fetchCalendarData: () => Promise<void>;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  category,
  onClick,
  userId,
  fetchCalendarData,
}) => {
  // Use userId as a prop if you don't have useAuth
  // const { user } = useAuth();
  const attendees = event.attendees || [];
  const userStatus = event.userStatus || {
    isFavorite: false,
    isAttendee: false,
  };
  const [favLoading, setFavLoading] = React.useState(false);
  const [attendingLoading, setAttendingLoading] = React.useState(false);
  const isFavorite = userStatus.isFavorite;
  const isAttending = userStatus.isAttendee;

  // Handler for favorite
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) return;
    setFavLoading(true);
    try {
      await setEventStatus(event.id, {
        isFavorite: !isFavorite,
        isAttendee: isAttending,
      });
      await fetchCalendarData();
    } catch (e) {
      // You can show an error toast
    } finally {
      setFavLoading(false);
    }
  };

  // Handler for attending
  const handleToggleAttending = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) return;
    setAttendingLoading(true);
    try {
      await setEventStatus(event.id, {
        isFavorite: isFavorite,
        isAttendee: !isAttending,
      });
      await fetchCalendarData();
    } catch (e) {
      // You can show an error toast
    } finally {
      setAttendingLoading(false);
    }
  };

  const startDate = event.startDate.toDate();
  const endDate = event.endDate ? event.endDate.toDate() : null;

  // Try to get the feature image URL (if present)
  const featureImageUrl =
    event.featuredImage && event.featuredImage.length > 0
      ? event.featuredImage[0].variants[0]?.url
      : null;

  return (
    <article
      className="flex bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition h-64"
      onClick={() => onClick && onClick(event)}
    >
      {/* Image or placeholder */}
      <div className="relative block w-24 sm:w-56 xl:sidebar-expanded:w-40 2xl:sidebar-expanded:w-56 shrink-0">
        {featureImageUrl ? (
          <img
            className="absolute object-cover object-center w-full h-full"
            src={featureImageUrl}
            alt={event.title}
            loading="lazy"
          />
        ) : (
          <div className="absolute flex flex-col items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-700 text-gray-400">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mb-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 7v10a4 4 0 004 4h10a4 4 0 004-4V7a4 4 0 00-4-4H7a4 4 0 00-4 4z"
              />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span className="text-xs">No Image</span>
          </div>
        )}
        {/* Like button opcional */}
        {userId && (
          <button
            className="absolute top-0 right-0 mt-4 mr-4"
            onClick={handleToggleFavorite}
            disabled={favLoading}
            aria-pressed={isFavorite}
          >
            <div className="text-gray-100 bg-gray-900 bg-opacity-60 rounded-full">
              <span className="sr-only">Like</span>
              {isFavorite ? (
                // Filled heart
                <svg className="h-8 w-8 fill-red-500" viewBox="0 0 32 32">
                  <path d="M22.682 11.318A4.485 4.485 0 0019.5 10a4.377 4.377 0 00-3.5 1.707A4.383 4.383 0 0012.5 10a4.5 4.5 0 00-3.182 7.682L16 24l6.682-6.318a4.5 4.5 0 000-6.364zm-1.4 4.933L16 21.247l-5.285-5A2.5 2.5 0 0112.5 12c1.437 0 2.312.681 3.5 2.625C17.187 12.681 18.062 12 19.5 12a2.5 2.5 0 011.785 4.251h-.003z" />
                </svg>
              ) : (
                // Empty heart
                <svg className="h-8 w-8 fill-current" viewBox="0 0 32 32">
                  <path d="M22.682 11.318A4.485 4.485 0 0019.5 10a4.377 4.377 0 00-3.5 1.707A4.383 4.383 0 0012.5 10a4.5 4.5 0 00-3.182 7.682L16 24l6.682-6.318a4.5 4.5 0 000-6.364zm-1.4 4.933L16 21.247l-5.285-5A2.5 2.5 0 0112.5 12c1.437 0 2.312.681 3.5 2.625C17.187 12.681 18.062 12 19.5 12a2.5 2.5 0 011.785 4.251h-.003z" />
                </svg>
              )}
            </div>
          </button>
        )}
      </div>
      {/* Content */}
      <div className="grow p-5 flex flex-col">
        <div className="grow">
          <div
            className="text-sm font-semibold uppercase mb-2"
            style={{ color: "#0070CC" }}
          >
            {/* Date of the event */}
            {startDate.toLocaleDateString(undefined, {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: event.timezone || undefined,
            })}
            {startDate && (
              <>
                {", "}
                {startDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: event.timezone || undefined,
                })}
              </>
            )}
            {endDate && (
              <>
                {" - "}
                {endDate.toLocaleDateString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  timeZone: event.timezone || undefined,
                })}
                ,{" "}
                {endDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: event.timezone || undefined,
                })}
              </>
            )}
            {event.timezone && (
              <span className="text-xs font-normal ml-1 opacity-70">
                ({getUtcOffsetLabel(event.timezone)})
              </span>
            )}
          </div>
          <div className="inline-flex mb-2">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              {event.title}
            </h3>
          </div>
          {event.description && (
            <div
              className="text-sm text-gray-700 dark:text-gray-200 mb-2 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          )}
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center mt-3">
          {/* Tag: category + author */}
          <div className="flex items-center gap-2 flex-wrap">
            {category && (
              <div className="text-xs inline-flex items-center font-medium border border-gray-200 dark:border-gray-700/60 text-gray-600 dark:text-gray-400 rounded-full text-center px-2.5 py-1">
                <span>{category.name}</span>
              </div>
            )}
            {event.createdByProfile && (
              <div className="flex items-center gap-1.5">
                <img
                  className="w-5 h-5 rounded-full object-cover"
                  src={getProfileImage(event.createdByProfile)}
                  alt={event.createdByProfile.name || "Author"}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                  {event.createdByProfile.name}
                </span>
              </div>
            )}
          </div>
          {/* Avatars of attendees, if any */}
          {attendees && attendees.length > 0 && (
            <div className="flex items-center space-x-2">
              <div
                className={`flex ${attendees.length > 1 ? "-space-x-3 -ml-0.5" : ""}`}
              >
                {attendees.slice(0, 3).map((att, idx) => (
                  <img
                    key={att.userId || att.id || idx}
                    className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 box-content object-cover"
                    src={att.avatarUrl || getProfileImage(att) || undefined}
                    alt={att.name || `User ${idx + 1}`}
                  />
                ))}
              </div>
              {attendees.length > 3 && (
                <div className="text-xs font-medium text-gray-400 dark:text-gray-500 italic">
                  +{attendees.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default EventCard;
